import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Download, MinusCircle, Truck, AlertCircle, MessageSquareWarning, CheckCircle2, XCircle, Clock } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import { pingNotifications } from '../lib/notificationsBus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_TYPE_LABELS = { salary: 'Salary', per_mile: 'Per Mile', hourly: 'Hourly', percentage: 'Percentage' };

const DEDUCTION_LABELS = {
  eld: 'ELD', parking: 'Parking', insurance: 'Insurance', lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental', dispatch_fee: 'Dispatch Fee', factoring_fee: 'Factoring Fee',
  truck_lease: 'Truck Lease Fee', fuel: 'Fuel Expense', other: 'Other Deduction',
  monthly_parking: 'Monthly Parking', daily_parking: 'Daily Parking', eld_fee: 'ELD Fee',
  toll: 'Toll', truck_wash: 'Truck Wash', repair: 'Repair', lease_on_fee: 'Lease-on Fee', trailer_fee: 'Trailer Fee',
};

const EXPENSE_CATEGORY_LABEL = {
  fuel: 'Fuel', toll: 'Toll', truck_wash: 'Truck Wash', repair: 'Repair',
  trip_repair: 'Trip Repair', ifta_tax: 'IFTA Tax', ifta_filing: 'IFTA Filing',
  plate_renewal: 'Plate Renewal', permit: 'Permit', drug_test: 'Drug Test',
  tax_2290: '2290 Tax', advance_repayment: 'Advance Repayment', other: 'Other',
};

const CORRECTION_CATEGORIES = [
  { value: 'missing_load', label: 'Missing Load' },
  { value: 'wrong_miles', label: 'Wrong Miles' },
  { value: 'wrong_rate', label: 'Wrong Rate' },
  { value: 'wrong_pay_type', label: 'Wrong Pay Structure' },
  { value: 'wrong_deduction', label: 'Wrong Deduction' },
  { value: 'wrong_advance', label: 'Wrong Advance Repayment' },
  { value: 'wrong_adjustment', label: 'Wrong Custom Adjustment' },
  { value: 'wrong_dates', label: 'Wrong Pay Period Dates' },
  { value: 'other', label: 'Other' },
];

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const netColor = (n) => ((Number(n) || 0) < 0 ? 'text-red-600' : 'text-green-700');
const CAT_LABELS = {
  eld: 'ELD', parking: 'Parking', insurance: 'Insurance', lease_on: 'Lease-on',
  trailer_rental: 'Trailer Rental', dispatch_fee: 'Dispatch Fee', factoring_fee: 'Factoring Fee',
  escrow: 'Escrow', legacy_balance: 'Prior Balance', toll: 'Toll', truck_wash: 'Truck Wash',
  repair: 'Repair', standing: 'Standing Deduction',
};
const REFUND_REASON_LABELS = {
  overpayment: 'Overpayment', contract_end: 'Contract End', adjustment: 'Adjustment', other: 'Other',
};
const refundTag = (rf) => REFUND_REASON_LABELS[rf.reason] || rf.notes;

const StatusPill = ({ status }) => {
  const cfg = {
    pending: { Icon: Clock, cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending' },
    approved: { Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Approved' },
    rejected: { Icon: XCircle, cls: 'bg-red-100 text-red-800 border-red-200', label: 'Rejected' },
    returned_for_revision: { Icon: MessageSquareWarning, cls: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Needs Revision' },
  }[status] || { Icon: Clock, cls: 'bg-slate-100 text-slate-700 border-slate-200', label: status };
  const { Icon, cls, label } = cfg;
  return (
    <span data-testid={`correction-status-${status}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border ${cls}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const PaystubDetail = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [commentsByCorrection, setCommentsByCorrection] = useState({}); // { correctionId: [comments] }
  const [replyDrafts, setReplyDrafts] = useState({}); // { correctionId: 'text' }
  const [replyingId, setReplyingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({ category: 'missing_load', description: '' });
  const [submittingCorrection, setSubmittingCorrection] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payrolls/me`, { withCredentials: true });
      const found = (res.data?.entries || []).find((e) => e.entry_id === entryId);
      if (!found) { setError('Pay period not found or not yours'); setLoading(false); return; }
      setEntry(found);
      const cRes = await axios.get(`${API}/payrolls/${found.payroll_id}/entries/${entryId}/corrections`, { withCredentials: true });
      setCorrections(cRes.data || []);
      // Fetch threaded comments per correction in parallel
      const allComments = await Promise.all(
        (cRes.data || []).map((c) =>
          axios.get(`${API}/corrections/${c.id}/comments`, { withCredentials: true })
            .then((r) => [c.id, r.data || []])
            .catch(() => [c.id, []])
        )
      );
      setCommentsByCorrection(Object.fromEntries(allComments));
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to load pay period');
    } finally {
      setLoading(false);
    }
  }, [entryId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDownload = async () => {
    if (!entry) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/payrolls/${entry.payroll_id}/entries/${entry.entry_id}/paystub.pdf`, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paystub_${entry.week_end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download paystub'); }
    finally { setDownloading(false); }
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    if (!correctionForm.description.trim()) { toast.error('Description is required'); return; }
    setSubmittingCorrection(true);
    try {
      await axios.post(
        `${API}/payrolls/${entry.payroll_id}/entries/${entry.entry_id}/corrections`,
        { category: correctionForm.category, description: correctionForm.description.trim() },
        { withCredentials: true }
      );
      toast.success('Correction request submitted');
      pingNotifications();
      setShowCorrectionForm(false);
      setCorrectionForm({ category: 'missing_load', description: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit correction');
    } finally { setSubmittingCorrection(false); }
  };

  const submitReply = async (correctionId) => {
    const body = (replyDrafts[correctionId] || '').trim();
    if (!body) { toast.error('Reply cannot be empty'); return; }
    setReplyingId(correctionId);
    try {
      await axios.post(
        `${API}/corrections/${correctionId}/comments`,
        { body },
        { withCredentials: true }
      );
      setReplyDrafts((p) => ({ ...p, [correctionId]: '' }));
      pingNotifications();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally { setReplyingId(null); }
  };

  if (loading) return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-8 text-red-600 flex items-start gap-2"><AlertCircle className="w-5 h-5 mt-0.5" /> {error}</div></DashboardLayout>;
  if (!entry) return null;

  return (
    <DashboardLayout>
      <div data-testid="paystub-detail-page" className="p-4 md:p-8 max-w-5xl mx-auto">
        <button onClick={() => navigate('/my-pay')} data-testid="back-to-my-pay" className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <ArrowLeft className="w-4 h-4" /> Back to My Pay
        </button>

        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#002FA7] mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pay Period</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                <Calendar className="w-7 h-7 text-slate-500" />
                {entry.week_start} → {entry.week_end}
              </h1>
              <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {entry.pay_type ? `${PAY_TYPE_LABELS[entry.pay_type]} @ ${entry.pay_type === 'percentage' ? `${entry.pay_rate}%` : `$${entry.pay_rate}`}` : 'No pay structure'}
                {' · '}{entry.load_count} load{entry.load_count === 1 ? '' : 's'}
                {entry.generated_at && <span> · Run {new Date(entry.generated_at).toLocaleString()}</span>}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Net Pay</p>
                <p data-testid="detail-net" className={`text-3xl font-bold ${netColor(entry.net_pay)}`} style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtMoney(entry.net_pay)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="detail-download-paystub" onClick={handleDownload} disabled={downloading} className="flex items-center gap-2 bg-[#002FA7] text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50">
                  <Download className="w-4 h-4" /> {downloading ? 'Downloading…' : 'Download PDF'}
                </button>
                <button data-testid="request-correction-button" onClick={() => setShowCorrectionForm(true)} className="flex items-center gap-2 bg-amber-100 text-amber-900 border border-amber-200 px-3 py-2 rounded-md text-sm font-semibold hover:bg-amber-200">
                  <MessageSquareWarning className="w-4 h-4" /> Request Correction
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="border border-slate-200 rounded-md p-3 bg-white"><p className="text-[11px] uppercase tracking-widest text-slate-500">Gross</p><p className="text-xl font-bold text-slate-900">{fmtMoney(entry.gross_pay)}</p></div>
          <div className="border border-slate-200 rounded-md p-3 bg-white"><p className="text-[11px] uppercase tracking-widest text-slate-500">+ Comp</p><p className="text-xl font-bold text-blue-700">{fmtMoney(entry.custom_compensation_total)}</p></div>
          <div className="border border-slate-200 rounded-md p-3 bg-white"><p className="text-[11px] uppercase tracking-widest text-slate-500">− Standing</p><p className="text-xl font-bold text-orange-700">{fmtMoney(entry.standing_deductions_total)}</p></div>
          <div className="border border-slate-200 rounded-md p-3 bg-white"><p className="text-[11px] uppercase tracking-widest text-slate-500">− Advances</p><p className="text-xl font-bold text-orange-700">{fmtMoney(entry.advance_repayments_total)}</p></div>
          <div className="border border-slate-200 rounded-md p-3 bg-white"><p className="text-[11px] uppercase tracking-widest text-slate-500">= Net</p><p className={`text-xl font-bold ${netColor(entry.net_pay)}`}>{fmtMoney(entry.net_pay)}</p></div>
        </div>

        {entry.net_pay < 0 && (
          <div data-testid="negative-net-banner" className="mb-6 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Deductions exceeded earnings this period. Net pay is <span className="font-bold">{fmtMoney(entry.net_pay)}</span>. The uncovered amount carries forward to future paychecks.
          </div>
        )}

        {/* Escrow snapshot */}
        {entry.escrow_snapshot && (
          <div data-testid="detail-escrow-card" className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Escrow</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
              <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Required</p><p className="text-lg font-bold text-slate-900">{fmtMoney(entry.escrow_snapshot.required)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Collected Before</p><p className="text-lg font-bold text-emerald-700">{fmtMoney(entry.escrow_snapshot.collected_before)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-500">This Period</p><p className="text-lg font-bold text-[#002FA7]">{fmtMoney(entry.escrow_snapshot.current_collected)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Remaining</p><p className="text-lg font-bold text-orange-700">{fmtMoney(entry.escrow_snapshot.remaining_after)}</p></div>
              <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Status</p><p className="text-lg font-bold text-slate-700">{entry.escrow_snapshot.status}</p></div>
            </div>
            {entry.escrow_snapshot.current_deferred > 0 && (
              <p className="text-xs text-red-600 mt-2">Escrow deferred this period: {fmtMoney(entry.escrow_snapshot.current_deferred)} (carried forward).</p>
            )}
          </div>
        )}

        {/* Escrow refunds (positive payout) */}
        {(entry.escrow_refunds_applied || []).length > 0 && (
          <div data-testid="detail-escrow-refund-card" className="bg-white border border-emerald-200 rounded-md p-6 mb-6">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Escrow Refunds</h2>
            {entry.escrow_refunds_applied.map((rf, i) => (
              <div key={`rf${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                <span className="text-slate-700">Escrow refund{refundTag(rf) ? <span className="text-xs text-slate-500 ml-2">{refundTag(rf)}</span> : null}</span>
                <span className="text-emerald-700 font-semibold">+ {fmtMoney(rf.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Deferred deductions */}
        {((entry.deferred_applied || []).length > 0 || (entry.deferred_created || []).length > 0) && (
          <div data-testid="detail-deferred-card" className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <h2 className="text-base font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Deferred Deductions</h2>
            {(entry.deferred_applied || []).length > 0 && (
              <div className="mb-3">
                <p className="text-[11px] uppercase tracking-widest text-emerald-700 mb-1">Prior deferred settled this period</p>
                {entry.deferred_applied.map((d, i) => (
                  <div key={`da${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">{CAT_LABELS[d.category] || d.category}{d.source_period_end ? <span className="text-xs text-slate-500 ml-2">from {d.source_period_end}</span> : null}</span>
                    <span className="text-emerald-700 font-semibold">− {fmtMoney(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {(entry.deferred_created || []).length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-red-700 mb-1">Deferred to next period (insufficient earnings)</p>
                {entry.deferred_created.map((d, i) => (
                  <div key={`dc${i}`} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700">{CAT_LABELS[d.category] || d.category}</span>
                    <span className="text-red-600 font-semibold">{fmtMoney(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Carry-forward */}
        {(entry.prior_balance_owed > 0 || entry.new_balance_owed > 0 || entry.balance_paydown > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3"><p className="text-[11px] uppercase tracking-widest text-amber-700">Prior Balance Owed</p><p className="text-lg font-bold text-amber-900">{fmtMoney(entry.prior_balance_owed)}</p></div>
            <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3"><p className="text-[11px] uppercase tracking-widest text-emerald-700">Balance Paid Down</p><p className="text-lg font-bold text-emerald-900">{fmtMoney(entry.balance_paydown)}</p></div>
            <div className={`border rounded-md p-3 ${entry.new_balance_owed > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}><p className={`text-[11px] uppercase tracking-widest ${entry.new_balance_owed > 0 ? 'text-red-700' : 'text-slate-500'}`}>Carries To Next</p><p className={`text-lg font-bold ${entry.new_balance_owed > 0 ? 'text-red-900' : 'text-slate-700'}`}>{fmtMoney(entry.new_balance_owed)}</p></div>
          </div>
        )}

        {/* Deductions list */}
        {((entry.standing_deductions || []).length > 0 || (entry.advance_repayments || []).length > 0 || (entry.adjustments || []).length > 0) && (
          <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MinusCircle className="w-4 h-4 text-orange-600" />
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Deductions & Adjustments</h2>
            </div>
            <div className="space-y-1.5">
              {(entry.standing_deductions || []).map((sd, i) => (
                <div key={`sd${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <span className="text-slate-700">{DEDUCTION_LABELS[sd.deduction_type] || sd.deduction_type}{sd.basis ? <span className="text-xs text-slate-500 ml-2">({sd.basis})</span> : null}</span>
                  <span className="text-orange-700 font-semibold">− {fmtMoney(sd.amount)}</span>
                </div>
              ))}
              {(entry.advance_repayments || []).map((ad, i) => (
                <div key={`ad${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <span className="text-slate-700">Cash Advance Repayment{ad.reason ? <span className="text-xs text-slate-500 ml-2">— {ad.reason}</span> : null}<span className="text-xs text-slate-500 ml-2">(balance after: {fmtMoney(ad.remaining_after)})</span></span>
                  <span className="text-orange-700 font-semibold">− {fmtMoney(ad.repaid_this_run)}</span>
                </div>
              ))}
              {(entry.adjustments || []).map((adj, i) => (
                <div key={`adj${i}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <span className="text-slate-700">{adj.adjustment_type === 'compensation' ? 'Bonus / Compensation' : (adj.expense_category ? (EXPENSE_CATEGORY_LABEL[adj.expense_category] || adj.expense_category) : 'Custom Deduction')}{adj.reason ? <span className="text-xs text-slate-500 ml-2">— {adj.reason}</span> : null}</span>
                  <span className={adj.adjustment_type === 'compensation' ? 'text-blue-700 font-semibold' : 'text-orange-700 font-semibold'}>{adj.adjustment_type === 'compensation' ? '+ ' : '− '}{fmtMoney(adj.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Correction request panel */}
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageSquareWarning className="w-4 h-4 text-amber-600" />
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Correction Requests {corrections.length > 0 ? `(${corrections.length})` : ''}</h2>
            </div>
            <button data-testid="open-correction-form" onClick={() => setShowCorrectionForm(true)} className="text-sm text-[#002FA7] font-semibold hover:underline">+ New Request</button>
          </div>

          {showCorrectionForm && (
            <form data-testid="correction-form" onSubmit={submitCorrection} className="border border-slate-200 rounded-md p-4 mb-4 bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</label>
                  <select data-testid="correction-category" value={correctionForm.category} onChange={(e) => setCorrectionForm({ ...correctionForm, category: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white">
                    {CORRECTION_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">What's wrong?</label>
                  <textarea data-testid="correction-description" value={correctionForm.description} onChange={(e) => setCorrectionForm({ ...correctionForm, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="e.g. Load ABC-123 delivered Tue 5/12 is missing from this pay period. PO 778899, 412 miles." required />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="submit-correction" type="submit" disabled={submittingCorrection} className="bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50">{submittingCorrection ? 'Submitting…' : 'Submit Request'}</button>
                <button type="button" onClick={() => { setShowCorrectionForm(false); setCorrectionForm({ category: 'missing_load', description: '' }); }} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
              </div>
            </form>
          )}

          {corrections.length === 0 ? (
            <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No correction requests for this pay period. If something looks off, click "New Request" to send it to your admin.</p>
          ) : (
            <div className="space-y-3">
              {corrections.map((c) => {
                const thread = commentsByCorrection[c.id] || [];
                const isTerminal = c.status === 'approved' || c.status === 'rejected';
                return (
                <div key={c.id} data-testid={`correction-row-${c.id}`} className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{c.category_label}</p>
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Submitted {new Date(c.created_at).toLocaleString()} by {c.requester_name}</p>
                      {c.first_viewed_at && (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          <CheckCircle2 className="w-3 h-3 inline-block mr-1 text-emerald-600" />
                          Viewed by admin {new Date(c.first_viewed_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <StatusPill status={c.status} />
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-line" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{c.description}</p>
                  {c.status === 'returned_for_revision' && c.admin_comment && (
                    <div data-testid={`returned-banner-${c.id}`} className="mt-2 p-2 border border-orange-200 bg-orange-50 rounded text-sm text-orange-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <p className="text-[11px] font-semibold uppercase tracking-widest mb-1">Admin needs more info</p>
                      {c.admin_comment}
                    </div>
                  )}
                  {(c.status === 'approved' || c.status === 'rejected') && c.admin_comment && (
                    <div className={`mt-2 p-2 border rounded text-sm ${c.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <p className="text-[11px] font-semibold uppercase tracking-widest mb-1">Admin reply{c.decided_by_name ? ` · ${c.decided_by_name}` : ''}</p>
                      {c.admin_comment}
                    </div>
                  )}

                  {/* Threaded conversation (excluding the kind='return' since it's already shown above) */}
                  {thread.filter(t => t.kind !== 'return').length > 0 && (
                    <div data-testid={`thread-${c.id}`} className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                      {thread.filter(t => t.kind !== 'return').map((t) => (
                        <div key={t.id} data-testid={`comment-${t.id}`} className={`p-2 rounded text-sm ${t.author_role === 'driver' || t.author_role === 'dispatcher' ? 'bg-slate-50 border border-slate-200' : 'bg-blue-50 border border-blue-200'}`}>
                          <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 text-slate-700">
                            {t.author_name} <span className="font-normal text-slate-500">· {t.author_role.replace('_', ' ')} · {new Date(t.created_at).toLocaleString()}</span>
                          </p>
                          <p className="text-slate-800 whitespace-pre-line" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{t.body}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply box — disabled when terminal (approved/rejected) */}
                  {!isTerminal && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <textarea
                        data-testid={`reply-input-${c.id}`}
                        value={replyDrafts[c.id] || ''}
                        onChange={(e) => setReplyDrafts((p) => ({ ...p, [c.id]: e.target.value }))}
                        rows={2}
                        placeholder={c.status === 'returned_for_revision' ? 'Reply with the requested info…' : 'Add a reply or extra context…'}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      />
                      <button
                        data-testid={`send-reply-${c.id}`}
                        onClick={() => submitReply(c.id)}
                        disabled={replyingId === c.id || !(replyDrafts[c.id] || '').trim()}
                        className="mt-1 bg-[#002FA7] text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-[#00247D] disabled:opacity-50"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {replyingId === c.id ? 'Sending…' : 'Send Reply'}
                      </button>
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}
        </div>

        {/* Loads delivered footer link */}
        <div className="mt-6">
          <Link to="/my-pay" data-testid="back-link-bottom" className="text-sm text-[#002FA7] font-semibold hover:underline"><Truck className="w-4 h-4 inline-block mr-1" /> All pay periods →</Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaystubDetail;
